import { useAuth } from "@sal/authentication.react";
import { configureWebRequest } from "@sal/webrequest-web";
import { refreshCv2Session } from "code/sessionRefresh";
import SessionExpiredPage from "pages/sessionExpired/SessionExpiredPage";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

/**
 * CV2-owned mid-session token refresh + session-expiry UX.
 *
 * ## Why this exists
 *
 * CV2's `SWA-A` session cookie expires after 1 hour. When that happens, `@sal/webrequest-web`'s
 * `sandfieldFetch` reacts to the resulting 401 by calling whatever `onRefreshToken` handler is
 * currently registered via `configureWebRequest`. Out of the box that's `@sal/portal`'s
 * `AuthConnector`, which wires up `@sal/authentication.react`'s own `refreshToken()`. That function
 * has two problems for CV2:
 *
 *  1. On success it calls `storeUserDetails(contactDetails)` with whatever
 *     `POST /PortalFramework/Account/Refresh` returns — a SALCore endpoint that never sees CV2's
 *     `X-Cv2-Account` header, so it always returns *base-contact* permissions. That silently wipes
 *     the account-scoped permissions `AccountProvider` deposited at mount (no Bookings, no
 *     PersonnelAndFleet), and the next in-app navigation bounces to `/NotFound`. See
 *     docs/architecture/auth-and-account-permissions.md.
 *  2. On failure its `catch` calls SAL's `logout()`, which flips `isLoggedIn` false and sends the
 *     user through `AuthenticatedRoute` -> `/Login` -> `RedirectToMyKrLogin` ->
 *     `window.location.replace("/Account/Login")` — bouncing straight to MyKR with no chance to
 *     show a "session expired" message, and losing whatever the user was doing.
 *
 * This component replaces `onRefreshToken` with a CV2-owned handler that (a) re-syncs account-scoped
 * permissions from `api/Accounts/ActivePermissions` after a successful refresh instead of leaving
 * base-contact permissions in place, and (b) on a genuinely failed refresh, shows a blocking
 * `SessionExpiredPage` instead of calling `logout()` — deliberately leaving `isLoggedIn` true and the
 * current permissions/URL untouched, so the user's in-progress work stays exactly where it was
 * underneath the dialog. Signing in again is the only way past it.
 *
 * The refresh-and-resync protocol itself (the POST + permissions resync in (a)) lives in
 * `code/sessionRefresh.ts` so `AccountProvider` can reuse the exact same recovery when its own raw
 * bootstrap `fetch()` calls hit a 401 at boot — see that module's doc comment.
 *
 * ## Winning the `configureWebRequest` registration race
 *
 * `configureWebRequest` is a module-level singleton in `@sal/webrequest-web` — last writer wins.
 * `AuthConnector` registers SAL's `refreshToken` in a `useEffect` and is an *ancestor* of this
 * component (`PortalFramework` renders `<AuthorisationContext><AuthConnector>{children}</AuthConnector></AuthorisationContext>`,
 * and `SessionExpiryProvider` lives inside those children). React fires child effects before parent
 * effects, so a plain `useEffect` here would register first and then be immediately clobbered by
 * AuthConnector's own effect running afterwards in the same commit.
 *
 * We win by deferring our registration with `setTimeout(..., 0)`: that macrotask only runs after the
 * entire synchronous effect-commit phase (including AuthConnector's effect) has finished, so our
 * registration always lands last.
 *
 * `@sal/authentication.react`'s `refreshToken` is **not memoised** — `setupAuthorisation()` is a
 * plain function invoked fresh on every render of `AuthorisationContext` (i.e. every time anything,
 * anywhere in the app, calls `storeUserDetails`/`setLoggedIn`/etc.), so it returns a brand new
 * `refreshToken` function identity each time. `AuthConnector`'s effect is keyed on exactly that
 * value, so it re-registers (re-clobbering us) every single time it changes. We mirror that same
 * dependency so our effect re-runs whenever AuthConnector's does, and re-defers a fresh registration
 * to win the race again — every time, not just once at boot.
 *
 * There's a brief window right after boot, before our deferred registration first lands, where SAL's
 * raw `refreshToken` is still the active handler. That's acceptable: nothing hits the API in that
 * window ahead of a user-initiated request, and by the time any 401 could plausibly occur,
 * `AccountProvider`'s own mount-time `ActivePermissions` sync (a separate, already-existing fix for
 * the boot-time SAL Refresh) plus this deferred registration have both long since settled.
 */
export function SessionExpiryProvider({ children }: { children: ReactNode }) {
	const [sessionExpired, setSessionExpired] = useState(false);
	const auth = useAuth();

	useEffect(() => {
		async function cv2RefreshToken(): Promise<boolean> {
			const ok = await refreshCv2Session(auth);
			if (!ok) {
				setSessionExpired(true);
			}
			return ok;
		}

		const timeoutId = window.setTimeout(() => {
			configureWebRequest({ onRefreshToken: cv2RefreshToken });
		}, 0);

		return () => window.clearTimeout(timeoutId);
		// Deliberately mirrors AuthConnector's own [refreshToken] dependency (see module comment
		// above) so we detect every re-registration it performs. `storeUserDetails`/`user` come
		// from the same setupAuthorisation() call as refreshToken and always change in lockstep
		// with it, so they don't need to be listed separately.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [auth.refreshToken]);

	return (
		<>
			{children}
			<SessionExpiredPage open={sessionExpired} />
		</>
	);
}
